import { useEffect, useState } from "react";
import { useAppDispatch } from "@grassrootly/rn-core";
import { GetCompetitionSeasons, GetOfficialCompetitions, ISeason } from "@grassrootly/api";
import { competitionsActions } from "../../api/competitions/CompetitionsReducer";
import { useCompetition, useCompetitionSeasons, useDefaultSeasonId } from "../../api/competitions/CompetitionsSelector";
import { MainStackScreenProps } from "../../navigation/Navigation.types";

export const COMPETITION_TABS = {
  standings: { alias: "standings" as const, title: "Standings" },
  topPlayers: { alias: "topPlayers" as const, title: "Top Players" },
  playerStats: { alias: "playerStats" as const, title: "Player Stats" },
  matches: { alias: "matches" as const, title: "Matches" },
};

export const useCompetitionDetail = (props: MainStackScreenProps<"competition-detail">) => {
  const { competitionId, initialTab } = props.route.params;
  let { competitionName } = props.route.params;
  
  // Decode URL-encoded competition name
  try {
    competitionName = decodeURIComponent(competitionName);
  } catch (e) {
    console.error("Error decoding competition name:", e);
  }
  
  const dispatch = useAppDispatch();

  const competition = useCompetition(competitionId);
  const seasons = useCompetitionSeasons(competitionId);
  const defaultSeasonId = useDefaultSeasonId(competitionId);

  const [selectedSeasonId, setSelectedSeasonId] = useState<number | null>(null);
  const [selectedTab, setSelectedTab] = useState<"standings" | "topPlayers" | "playerStats" | "matches">(initialTab || "standings");

  const tabs = [COMPETITION_TABS.standings, COMPETITION_TABS.topPlayers, COMPETITION_TABS.playerStats, COMPETITION_TABS.matches];

  // Fetch competition data if not in Redux
  useEffect(() => {
    const fetchCompetition = async () => {
      try {
        const competitions = await GetOfficialCompetitions();
        dispatch(competitionsActions.setCompetitions(competitions));
      } catch (error) {
        console.error("Error fetching competition:", error);
      }
    };

    if (!competition) {
      fetchCompetition();
    }
  }, [competitionId, competition]);

  // Fetch seasons on mount
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const data = await GetCompetitionSeasons({ competitionId });
        dispatch(
          competitionsActions.setSeasons({
            competitionId,
            seasons: data.seasons,
            defaultSeasonId: data.defaultSeasonId,
          })
        );
      } catch (error) {
        console.error("Error fetching seasons:", error);
      }
    };

    if (seasons.length === 0) {
      fetchSeasons();
    }
  }, [competitionId]);

  // Set selected season when default season is available
  useEffect(() => {
    if (defaultSeasonId && !selectedSeasonId) {
      setSelectedSeasonId(defaultSeasonId);
    }
  }, [defaultSeasonId]);

  const onTabSelected = (alias: string) => {
    // Only allow known tab aliases to update state
    if (alias === "standings" || alias === "topPlayers" || alias === "playerStats" || alias === "matches") {
      setSelectedTab(alias);
    }
  };

  const onSeasonSelected = (seasonId: number) => {
    setSelectedSeasonId(seasonId);
  };

  const handleNavigationPress = () => {
    if (props.navigation.canGoBack()) {
      props.navigation.goBack();
    } else {
      props.navigation.navigate("competitions-list");
    }
  };

  return {
    competitionId,
    competitionName,
    competition,
    seasons,
    selectedSeasonId,
    selectedTab,
    tabs,
    onTabSelected,
    onSeasonSelected,
    handleNavigationPress,
  };
};
